import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AppShell from '../components/AppShell';
import Toast from '../components/Toast';
import api, { resolveAvatarUrl } from '../api';
import { useAuth } from '../context/AuthContext';

/**
 * Refer friends page — invite code, share link, referral stats.
 * Split out from AccountSettings on 2026-07-15.
 */
export default function ReferralsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [toast, setToast] = useState(null);
  const [copied, setCopied] = useState(false);
  const [refStats, setRefStats] = useState({ referrals_count: 0, commission_earned: 0 });
  const [referrals, setReferrals] = useState(null); // null → loading, [] → empty, [{...}] → list
  const [manualCopy, setManualCopy] = useState(null); // { text, label } — shown when both copy paths fail

  useEffect(() => {
    api.get('/profile/referral-stats')
      .then(({ data }) => setRefStats({
        referrals_count: data.referrals_count || 0,
        commission_earned: data.commission_earned || 0,
      }))
      .catch(() => {});
    api.get('/profile/referred-users')
      .then(({ data }) => setReferrals(Array.isArray(data.referrals) ? data.referrals : []))
      .catch(() => setReferrals([]));
  }, []);

  const referralLink = useMemo(() => {
    const base = typeof window !== 'undefined' ? window.location.origin : 'https://clanster.app';
    return `${base}/onboarding?code=${user?.referral_code || ''}`;
  }, [user]);

  /**
   * Robust copy that survives sandboxed iframe environments (Emergent preview,
   * some in-app browsers) where navigator.clipboard is present but throws
   * NotAllowedError. Falls back to a hidden-but-selectable textarea, then to a
   * manual-copy modal as ultimate rescue so the user is never dead-ended.
   */
  const copyText = async (text, msg, label = 'link') => {
    // Path 1 — modern Async Clipboard API
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        setToast({ msg });
        return;
      }
    } catch {
      // fall through
    }
    // Path 2 — legacy execCommand fallback with a properly-styled textarea
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      // Must be visible-ish AND non-scrolling to survive Safari/Firefox rules
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '0';
      ta.style.left = '0';
      ta.style.width = '2em';
      ta.style.height = '2em';
      ta.style.padding = '0';
      ta.style.border = 'none';
      ta.style.outline = 'none';
      ta.style.boxShadow = 'none';
      ta.style.background = 'transparent';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, text.length);
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) {
        setToast({ msg });
        return;
      }
    } catch {
      // fall through
    }
    // Path 3 — ultimate rescue: show the text in a modal so user can copy manually
    setManualCopy({ text, label });
  };

  const copyReferralCode = async () => {
    const code = user?.referral_code || '';
    await copyText(code, `✓ Code ${code} copied`, 'invite code');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <AppShell activeTab="wallet">
      <div className="settings-wrap" data-testid="referrals-page">
        <h1 className="settings-title">Refer friends</h1>
        <p className="settings-subtitle">
          Share your invite — you earn <b>1%</b> of every trade your referrals make, forever.
        </p>

        <div className="referral-card" data-testid="referrals-card">
          <div className="ref-code-row">
            <div>
              <div className="ref-label">Your invite code</div>
              <div className="ref-code" data-testid="referrals-code">{user?.referral_code || '——'}</div>
            </div>
            <button
              className={`ref-copy-btn${copied ? ' done' : ''}`}
              onClick={copyReferralCode}
              data-testid="referrals-copy-code-btn"
            >
              {copied ? '✓ Copied' : 'Copy code'}
            </button>
          </div>
          <div className="ref-stats">
            <div className="ref-stat" data-testid="referrals-count">
              <div className="ref-stat-value">{refStats.referrals_count}</div>
              <div className="ref-stat-label">People joined</div>
            </div>
            <div className="ref-stat" data-testid="referrals-earned">
              <div className="ref-stat-value">{refStats.commission_earned.toFixed(2)} pts</div>
              <div className="ref-stat-label">Commission earned</div>
            </div>
          </div>
          <button
            className="ref-link-btn"
            onClick={() => copyText(referralLink, '✓ Referral link copied', 'invite link')}
            data-testid="referrals-copy-link-btn"
          >
            <svg viewBox="0 0 16 16" fill="none" width="14" height="14">
              <path d="M7 9l2 2m-1-6l3 3m-1-3l-4 4a2.8 2.8 0 004 4l1-1m-4-2l4-4a2.8 2.8 0 00-4-4l-1 1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
            Copy full invite link
          </button>
        </div>

        {/* Referred-users list — who joined, when, and how much you've earned from each. */}
        <ReferralList referrals={referrals} data-testid="referral-list" />

        <button
          className="btn-ghost"
          style={{ marginTop: 8 }}
          onClick={() => navigate(-1)}
          data-testid="referrals-back-btn"
        >
          ← Back
        </button>
      </div>

      {toast && <Toast msg={toast.msg} onDone={() => setToast(null)} />}
      {manualCopy && (
        <ManualCopyModal
          text={manualCopy.text}
          label={manualCopy.label}
          onClose={() => setManualCopy(null)}
        />
      )}
    </AppShell>
  );
}

/**
 * Manual-copy rescue modal shown only when BOTH clipboard APIs fail (sandboxed
 * iframe, clipboard permissions blocked, etc.). Pre-selects the text so the
 * user only needs to press Ctrl/Cmd+C.
 */
function ManualCopyModal({ text, label, onClose }) {
  const inputRef = React.useRef(null);
  useEffect(() => {
    // Auto-select the text so user can Ctrl/Cmd+C immediately
    const t = setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 50);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="manual-copy-backdrop">
      <div className="modal-card" onClick={(e) => e.stopPropagation()} data-testid="manual-copy-modal" style={{ maxWidth: 440 }}>
        <button className="modal-close" onClick={onClose} aria-label="Close" data-testid="manual-copy-close-btn">×</button>
        <h2 className="heading" style={{ textAlign: 'left', fontSize: 20 }}>Copy your <span className="grad">{label}</span></h2>
        <p className="subcopy" style={{ textAlign: 'left', margin: '4px 0 16px' }}>
          Your browser blocked automatic copy. Select the text below and press <b>Ctrl/Cmd + C</b> to copy manually.
        </p>
        <input
          ref={inputRef}
          type="text"
          className="field-input"
          value={text}
          readOnly
          onFocus={(e) => e.target.select()}
          data-testid="manual-copy-input"
          style={{ width: '100%', fontFamily: 'monospace', fontSize: 13 }}
        />
        <button
          className="btn-primary"
          onClick={onClose}
          data-testid="manual-copy-done-btn"
          style={{ marginTop: 16 }}
        >
          Done
        </button>
      </div>
    </div>
  );
}


/**
 * Referred-users list — shows every account that signed up with the caller's
 * code, plus per-user trading activity and the commission earned from each.
 * Rendered as a card grid on mobile / table-like rows on desktop.
 */
function ReferralList({ referrals }) {
  // Loading state — before the API responds
  if (referrals === null) {
    return (
      <section className="rl-section" data-testid="referral-list-loading">
        <div className="rl-heading">Your referrals</div>
        <div className="rl-empty">Loading…</div>
      </section>
    );
  }

  // Empty state — user hasn't referred anyone yet
  if (referrals.length === 0) {
    return (
      <section className="rl-section" data-testid="referral-list-empty">
        <div className="rl-heading">Your referrals</div>
        <div className="rl-empty">
          <div className="rl-empty-emoji" aria-hidden="true">👥</div>
          <div className="rl-empty-title">Nobody's used your code yet</div>
          <div className="rl-empty-sub">
            Share the invite link above — you'll see everyone who joins with your code here,
            along with the commission you've earned from their trades.
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="rl-section" data-testid="referral-list">
      <div className="rl-heading">
        Your referrals <span className="rl-count">· {referrals.length}</span>
      </div>
      <div className="rl-list">
        {referrals.map((r) => <ReferralRow key={r.id} r={r} />)}
      </div>
    </section>
  );
}

function ReferralRow({ r }) {
  const avatarUrl = resolveAvatarUrl(r.avatar);
  const joined = r.joined_at ? formatJoined(r.joined_at) : '—';
  const hasHandle = !!(r.handle && r.handle.trim());
  const initials = ((r.display_name || r.handle || '?').trim().slice(0, 1) || '?').toUpperCase();
  const displayName = hasHandle ? r.display_name : 'Pending signup';
  const subline = hasHandle
    ? `@${r.handle} · joined ${joined}`
    : `Hasn't finished onboarding · signed up ${joined}`;

  // Inner content — reused for both linked and static variants
  const body = (
    <>
      <div className="rl-avatar" aria-hidden="true">
        {avatarUrl
          ? <img src={avatarUrl} alt="" loading="lazy" />
          : <span>{initials}</span>
        }
      </div>
      <div className="rl-identity">
        <div className="rl-name" title={displayName}>{displayName}</div>
        <div className="rl-handle">{subline}</div>
      </div>
      <div className="rl-metrics">
        <div className="rl-metric">
          <div className="rl-metric-value" data-testid={`referral-trades-${r.id}`}>{r.trades_count}</div>
          <div className="rl-metric-label">trades</div>
        </div>
        <div className="rl-metric rl-metric-earn">
          <div className="rl-metric-value" data-testid={`referral-earned-${r.id}`}>+{r.commission_earned.toFixed(2)} pts</div>
          <div className="rl-metric-label">you earned</div>
        </div>
      </div>
    </>
  );

  // Only link to /creator/:handle when the referred user has a valid handle
  // (i.e., completed onboarding). Otherwise render as a non-clickable card.
  if (hasHandle) {
    return (
      <Link to={`/creator/${r.handle}`} className="rl-row" data-testid={`referral-row-${r.handle}`}>
        {body}
      </Link>
    );
  }
  return (
    <div className="rl-row rl-row-pending" data-testid={`referral-row-pending-${r.id}`}>
      {body}
    </div>
  );
}

/** Compact "5d ago" / "3mo ago" / "Feb 12" formatter tuned for this list. */
function formatJoined(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const now = Date.now();
  const diffMs = now - d.getTime();
  const day = 1000 * 60 * 60 * 24;
  const days = Math.floor(diffMs / day);
  if (days < 1) return 'today';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}
